import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Ring() {
  const [members, setMembers] = useState(null);
  useEffect(() => { api.get("/ring").then((r) => setMembers(r.data.members || [])).catch(() => setMembers([])); }, []);

  return (
    <div data-testid="ring-page">
      <Seo title="The Voluntold Ring" description="Recognizing the coaches, team parents, and volunteers who hold youth sports together — the people who never make the box score but everybody would miss." path="/ring" />
      <section className="bg-[#222222] border-b-4 border-[#DB9130]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <span className="font-head uppercase tracking-[0.25em] text-xs text-[#DB9130]">Recognition</span>
          <h1 className="mt-3 font-head font-bold uppercase text-4xl sm:text-5xl text-[#F5F0EA] leading-[1.05]">The Voluntold Ring</h1>
          <p className="mt-5 max-w-2xl text-[#C7D0D6] text-lg leading-relaxed">
            They may never get their name announced. They may never appear in the box score. But take them away and everybody notices.
          </p>
          <Link to="/nominate" className="mt-6 inline-block bg-[#DB9130] text-[#222222] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#C67F22] transition-colors" data-testid="ring-nominate-cta">Nominate someone</Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        {members === null ? (
          <p className="text-[#6E6E6E]">Loading…</p>
        ) : members.length === 0 ? (
          <div className="border-2 border-dashed border-[#D3CFC0] bg-[#F5F0EA] p-10 text-center" data-testid="ring-empty">
            <span className="font-head uppercase tracking-[0.2em] text-sm text-[#DB9130]">Ring #001</span>
            <p className="mt-3 font-head uppercase text-xl text-[#222222]">The first honoree is coming.</p>
            <p className="mt-2 text-[#6E6E6E] text-sm max-w-md mx-auto">Know someone who holds their program together? Put their name forward.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="ring-list">
            {members.map((m, i) => (
              <article key={i} className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-5 border-2 border-[#222222] bg-[#F5F0EA] p-5" data-testid={`ring-member-${m.ring_number || i}`}>
                <div className="w-[120px] h-[120px] bg-[#E7E3D9] overflow-hidden">
                  {m.photo_url ? <img src={m.photo_url} alt={m.nominee} className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center font-head text-[#B7AE9E] text-2xl">VR</div>}
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-head uppercase tracking-wide text-xs text-[#DB9130]">Ring #{m.ring_number || "—"}</span>
                    {m.year && <span className="text-xs text-[#6E6E6E]">{m.year}</span>}
                  </div>
                  <h2 className="mt-1 font-head font-semibold text-2xl text-[#222222] leading-tight">{m.nominee}</h2>
                  {m.city_state && <p className="text-sm text-[#6E6E6E]">{m.city_state}</p>}
                  {m.pull_quote && <p className="mt-3 text-[#2D2D2D] italic leading-relaxed border-l-2 border-[#DB9130] pl-4">"{m.pull_quote}"</p>}
                  {m.video_url && (
                    <a href={m.video_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-[#222222] font-head uppercase tracking-wide text-sm link-line" data-testid={`ring-video-${i}`}>
                      <PlayCircle size={18} /> Watch their story
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
